import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { DecisionDialog } from "../../components/portal/DecisionDialog";
import { ClientUpdatesList, AddDetailsButton } from "../../components/portal/ClientUpdates";
import { PortalPage, Section, Pill } from "../../components/portal/PortalPage";
import { ProgressStepper } from "../../components/portal/ProgressStepper";
import { NoticeBox } from "../../components/NoticeBox";
import { AttachmentGallery } from "../../components/portal/AttachmentGallery";
import { ClientTimeline } from "../../components/portal/ClientTimeline";
import { ClientNotesThread } from "../../components/portal/ClientNotesThread";
import { usePortal } from "../../components/portal/usePortal";
import { useBrandName, useTeamLabel } from "../../components/portal/PortalBrand";
import { fmtDate, fmtDateTime, fmtMoney, requestStatusMap, JOB_STATUS, PROJECT_STATUS, titleCase } from "../../components/portal/portalUtils";
import { downloadAuthFile } from "../../lib/fileOpen";
import { ShieldCheck, Briefcase, DownloadSimple, ChatCircle, MapPin, Phone, ArrowRight, Kanban, ListChecks } from "@phosphor-icons/react";
import { ScopeText, scopeLines } from "../../components/jobs/ScopeOfWorksField";
import { DetailSkeleton } from "../../components/Skeletons";
import { toast } from "sonner";

export default function ClientRequestDetailPage() {
  const { id } = useParams();
  const { perms } = usePortal();
  const teamLabel = useTeamLabel();
  const brandName = useBrandName();
  const [jr, setJr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState(null);

  const load = useCallback(() => API.get(`/client-hub/requests/${id}`).then(({ data }) => setJr(data)).catch(() => setJr(false)), [id]);
  useEffect(() => { load(); }, [load]);

  const respond = async ({ notes, po_number }) => {
    setBusy(true);
    try {
      await API.post(`/client-hub/requests/${id}/approve-quote`, { decision, notes, po_number });
      toast.success(decision === "approve" ? "Quote approved — we'll schedule the work" : "Quote declined");
      setDecision(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (jr === null) return <DetailSkeleton />;
  if (jr === false) return <PortalPage title="Request not found" back={{ to: "/portal/requests", label: "Requests" }} />;

  const awaiting = jr.status === "submitted_to_client" && jr.selected_quote;
  const isProject = jr.request_type === "project";

  return (
    <PortalPage
      title={jr.title}
      subtitle={<span className="flex items-center gap-2 flex-wrap"><span className="font-mono">{jr.reference}</span><Pill map={requestStatusMap(jr)} value={jr.status} />{isProject && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 font-semibold flex items-center gap-1" data-testid="detail-project-badge"><Kanban size={11} /> Project Request</span>}{jr.mandate && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 font-semibold flex items-center gap-1" data-testid="detail-mandate-badge"><ShieldCheck size={11} /> Mandate up to {fmtMoney(jr.mandate_limit)}</span>}</span>}
      back={{ to: "/portal/requests", label: "Requests" }}
      testid="request-detail"
      actions={<>
        {jr.job && <Button size="sm" variant="outline" asChild><Link to={`/portal/jobs/${jr.job.id}`} data-testid="open-job-btn"><Briefcase size={14} className="mr-1" /> Open Job {jr.job.reference}</Link></Button>}
        {jr.project && <Button size="sm" asChild><Link to={`/portal/projects/${jr.project.id}`} data-testid="open-project-btn"><Kanban size={14} className="mr-1" /> Open Project {jr.project.reference}</Link></Button>}
      </>}
    >
      <NoticeBox notice={jr.notice} className="mb-6" testid="request-notice" />
      <Section title="Progress" className="mb-6" testid="progress-section">
        <ProgressStepper progress={jr.progress} />
        {jr.handled_by && <p className="text-[11px] text-muted-foreground mt-3" data-testid="handled-by">Being handled by <strong>{jr.handled_by}</strong> at {brandName}</p>}
      </Section>

      {awaiting && (
        <div className="mb-6 border-2 border-purple-400 dark:border-purple-700 rounded-lg bg-purple-50 dark:bg-purple-950/30 p-5" data-testid="quote-approval-card">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest font-semibold text-purple-700 dark:text-purple-300">Quote ready for your approval</p>
              <p className="text-3xl font-bold font-[Chivo] mt-1" data-testid="quote-total">{fmtMoney(jr.selected_quote.client_total)} <span className="text-xs font-normal text-muted-foreground">ex VAT</span></p>
              {jr.selected_quote.description && <p className="text-xs text-muted-foreground mt-1">{jr.selected_quote.description}</p>}
              <p className="text-[11px] text-muted-foreground mt-1">
                {jr.selected_quote.validity_date ? `Valid until ${fmtDate(jr.selected_quote.validity_date)}` : ""}
                {jr.selected_quote.proposed_start_date ? ` · Proposed start ${fmtDate(jr.selected_quote.proposed_start_date)}` : ""}
              </p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => downloadAuthFile(`/api/client-hub/requests/${id}/quote-pdf`, `${jr.reference}_quote.pdf`)} data-testid="quote-pdf-btn"><DownloadSimple size={14} className="mr-1" /> Download PDF</Button>
              {perms.can_approve ? (
                <div className="flex gap-2">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 flex-1" onClick={() => setDecision("approve")} data-testid="approve-quote-btn">Approve</Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-300 flex-1" onClick={() => setDecision("decline")} data-testid="decline-quote-btn">Decline</Button>
                </div>
              ) : <p className="text-[11px] text-muted-foreground max-w-[200px]">Only portal administrators can approve quotes — ask your admin to sign off.</p>}
            </div>
          </div>
        </div>
      )}

      {jr.selected_quote && !awaiting && (
        <div className="mb-6 border border-border rounded-lg bg-card p-4 flex items-center justify-between gap-3" data-testid="quote-summary-card">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{jr.client_response?.decision === "decline" ? "Declined quote" : "Approved quote"}</p>
            <p className="text-lg font-bold font-[Chivo]">{fmtMoney(jr.selected_quote.client_total)} <span className="text-xs font-normal text-muted-foreground">ex VAT</span></p>
            {jr.client_response && <p className="text-[11px] text-muted-foreground" data-testid="quote-decision-meta">{jr.client_response.decision === "decline" ? "Declined" : "Approved"} by {jr.client_response.name} · {fmtDateTime(jr.client_response.at)}{jr.client_response.po_number ? ` · PO ${jr.client_response.po_number}` : ""}</p>}
          </div>
          <Button variant="outline" size="sm" onClick={() => downloadAuthFile(`/api/client-hub/requests/${id}/quote-pdf`, `${jr.reference}_quote.pdf`)} data-testid="quote-pdf-btn"><DownloadSimple size={14} className="mr-1" /> PDF</Button>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {(scopeLines(jr.scope_of_works).length > 0 || scopeLines(jr.caveats).length > 0) && (
            <Section title={<span className="flex items-center gap-1.5"><ListChecks size={14} /> Scope of Works</span>} testid="request-scope-of-works">
              <ScopeText text={jr.scope_of_works} testid="request-scope-of-works-text" />
              {scopeLines(jr.caveats).length > 0 && (
                <div className="mt-3 border-l-[3px] border-amber-400 bg-amber-50/70 dark:bg-amber-500/10 rounded-r-md px-3 py-2" data-testid="request-caveats">
                  <p className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-semibold mb-1">Caveats & exclusions</p>
                  <ScopeText text={jr.caveats} testid="request-caveats-text" />
                </div>
              )}
            </Section>
          )}
          <Section title="Request Details" testid="request-details-section"
            action={jr.can_add_details && perms.can_message ? <AddDetailsButton requestId={id} onAdded={load} /> : null}>
            <p className="text-sm whitespace-pre-wrap">{jr.request_details || <span className="text-muted-foreground">No further details given.</span>}</p>
            <ClientUpdatesList updates={jr.client_updates} viewer="client" />
            {!jr.can_add_details && (jr.client_updates || []).length === 0 && <p className="text-[11px] text-muted-foreground mt-2">This request is closed — use the conversation below to message our team.</p>}
            <dl className="grid sm:grid-cols-2 gap-3 mt-4 text-xs">
              <div><dt className="text-muted-foreground">Category</dt><dd className="font-medium">{titleCase(jr.category)}</dd></div>
              <div><dt className="text-muted-foreground">Priority</dt><dd className="font-medium">{titleCase(jr.priority)}</dd></div>
              <div><dt className="text-muted-foreground">Raised</dt><dd className="font-medium">{fmtDateTime(jr.created_at)}{jr.created_by_name ? ` by ${jr.created_by_name.replace(" (Client)", "")}` : ""}{jr.on_behalf_by_client_name ? <span className="block text-[11px] text-sky-700 dark:text-sky-300 font-normal" data-testid="request-on-behalf">Raised by {jr.on_behalf_by_client_name}{jr.for_client ? ` on behalf of ${jr.for_client}` : " on your behalf"}</span> : null}</dd></div>
              <div><dt className="text-muted-foreground">Target completion</dt><dd className="font-medium">{fmtDate(jr.requested_completion_date)}</dd></div>
              {isProject && <div><dt className="text-muted-foreground">Desired start</dt><dd className="font-medium">{fmtDate(jr.desired_start_date)}</dd></div>}
              {isProject && jr.estimated_budget > 0 && <div><dt className="text-muted-foreground">Indicative budget</dt><dd className="font-medium">{fmtMoney(jr.estimated_budget)}</dd></div>}
              {jr.mandate && <div className="sm:col-span-2"><dt className="text-muted-foreground">Mandate conditions</dt><dd className="font-medium">{jr.mandate_note || "None"}</dd></div>}
            </dl>
          </Section>

          <Section title={`Photos & Files (${(jr.attachments || []).length})`}>
            <AttachmentGallery attachments={jr.attachments || []} />
          </Section>

          {jr.project && (
            <Section title="Live Project" testid="linked-project-section">
              <Link to={`/portal/projects/${jr.project.id}`} className="flex items-center gap-3 group" data-testid="linked-project-link">
                <div className="h-10 w-10 rounded-md bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center text-violet-600"><Kanban size={20} weight="duotone" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold group-hover:text-primary">{jr.project.reference} · {jr.project.name}</p>
                  <p className="text-[11px] text-muted-foreground">Follow tasks, milestones and photos in your Projects section</p>
                </div>
                <Pill map={PROJECT_STATUS} value={jr.project.status} />
                <ArrowRight size={16} className="text-muted-foreground" />
              </Link>
            </Section>
          )}

          {jr.job && (
            <Section title="Linked Job" testid="linked-job-section">
              <Link to={`/portal/jobs/${jr.job.id}`} className="flex items-center gap-3 group" data-testid="linked-job-link">
                <div className="h-10 w-10 rounded-md bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-emerald-600"><Briefcase size={20} weight="duotone" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold group-hover:text-primary">{jr.job.reference} · {jr.job.title}</p>
                  <p className="text-[11px] text-muted-foreground">{jr.job.scheduled_start ? `Scheduled ${fmtDateTime(jr.job.scheduled_start)}` : "Awaiting schedule"}{jr.job.contractor ? ` · ${jr.job.contractor}` : jr.job.contractor_assigned ? ` · ${teamLabel.assigned}` : ""}</p>
                </div>
                <Pill map={JOB_STATUS} value={jr.job.status} />
                <ArrowRight size={16} className="text-muted-foreground" />
              </Link>
            </Section>
          )}

          <Section title={<span className="flex items-center gap-1.5"><ChatCircle size={14} /> Conversation</span>} testid="comments-section">
            <ClientNotesThread items={jr.client_comments || []} postUrl={`/client-hub/requests/${id}/comments`} folder="client-requests" canMessage={perms.can_message} onSent={load} testPrefix="comment" />
          </Section>
        </div>

        <div className="space-y-6">
          <Section title="Property">
            <p className="text-sm font-medium flex items-start gap-1.5"><MapPin size={16} className="text-primary shrink-0 mt-0.5" /> {jr.property_name ? `${jr.property_name} — ` : ""}{jr.address}</p>
            {jr.what3words && <p className="text-[11px] text-muted-foreground mt-1">///{jr.what3words}</p>}
            {(jr.site_contact_name || jr.site_contact_phone) && <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Phone size={12} /> {jr.site_contact_name} {jr.site_contact_phone}</p>}
          </Section>
          <Section title="Activity"><ClientTimeline entries={jr.timeline} /></Section>
        </div>
      </div>

      <DecisionDialog decision={decision ? { kind: decision, total: jr.selected_quote?.client_total, label: `"${jr.title}"` } : null} onClose={() => setDecision(null)} onConfirm={respond} busy={busy} testPrefix="decision" />
    </PortalPage>
  );
}
